import {
    Box,
    Button,
    ButtonGroup,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React from 'react';
import { useTypedSelector } from '../../../../store/hooks';
import TableToExcel from '@linways/table-to-excel';
import langFile from '../../../../data/lang.json';
import { theme } from '../../../../styles/theme';
import { setType } from '../../../../store/features/loans/loans.slice';
import { useDispatch } from 'react-redux';

const PaymentSchedule: React.FC = (props) => {
    const dispatch = useDispatch();
    const { period, annuity, differentiated, type } = useTypedSelector((state) => state.loans);
    const { lang } = useTypedSelector((state) => state.common);
    const types = [
        (langFile.paymentTypes.annuity as any)[lang],
        (langFile.paymentTypes.differentiated as any)[lang],
    ];

    const prepareItems = () => {
        if (!annuity.totalAmount) return [];
        let items = [];
        let today = new Date();
        const startDate = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            today.getDate(),
        ).toLocaleDateString();
        let total = type === 0 ? annuity.totalAmount : differentiated.totalAmount;

        for (let i = 0; i < period; i++) {
            let nextDate = new Date(
                today.getFullYear(),
                today.getMonth() + 1 + i,
                today.getDate(),
            ).toLocaleDateString();
            const monthlyPayment = type === 0 ? annuity.monthlyPayment : differentiated.monthlyPayment;

            items.push({
                order: i + 1,
                date: i === 0 ? startDate : nextDate,
                payment: monthlyPayment,
                balance: i === period - 1 ? 0 : (total -= monthlyPayment),
            });
        }
        return items;
    };

    const downloadToExcel = () => {
        TableToExcel.convert(document.getElementById('payments-schedule'), {
            name: 'schedule.xlsx',
            sheet: {
                name: 'Sheet 1',
            },
        });
    };

    const Excel = () => {
        return (
            <svg style={{ width: 24, height: 24 }} viewBox="0 0 24 24">
                <path
                    fill="#1D6F42"
                    d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M15.8,20H14L12,16.6L10,20H8.2L11.1,15.5L8.2,11H10L12,14.4L14,11H15.8L12.9,15.5L15.8,20M13,9V3.5L18.5,9H13Z"
                />
            </svg>
        );
    };

    return prepareItems().length > 0 ? (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    pb: 1,
                }}
            >
                <Typography variant="h6">{(langFile.paymentSchedule as any)[lang]}</Typography>
                <Tooltip title={(langFile.download as any)[lang]} placement="top">
                    <IconButton onClick={downloadToExcel}>
                        <Excel />
                    </IconButton>
                </Tooltip>
            </Stack>
            <TableContainer elevation={0} component={Paper}>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                    <ButtonGroup size="small">
                        {types.map((item, index) => (
                            <Button
                                key={item}
                                disableElevation
                                variant={type === index ? 'contained' : undefined}
                                onClick={() => dispatch(setType(index))}
                            >
                                {item}
                            </Button>
                        ))}
                    </ButtonGroup>
                </Stack>
                <Table stickyHeader id="payments-schedule" data-cols-width="10, 15, 20, 20">
                    <TableHead>
                        <TableRow
                            sx={{
                                '& th': {
                                    color: '#fff',
                                    background: theme.palette.primary.main,
                                    border: '1px solid rgba(224, 224, 224, 1)',
                                    py: 0.5,
                                },
                            }}
                        >
                            <TableCell data-b-a-s="thin" data-a-h="center">
                                {(langFile.period as any)[lang]}
                            </TableCell>
                            <TableCell data-b-a-s="thin" data-a-h="center">
                                {(langFile.tableDate as any)[lang]}
                            </TableCell>
                            <TableCell data-b-a-s="thin" data-a-h="center">
                                {(langFile.tableOrder as any)[lang]}
                            </TableCell>
                            <TableCell data-b-a-s="thin" data-a-h="center">
                                {(langFile.tablePaymentBalance as any)[lang]}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow
                            sx={{
                                '& td': {
                                    border: '1px solid rgba(224, 224, 224, 1)',
                                    backgroundColor: '#eee',
                                    py: 1,
                                },
                            }}
                        >
                            <TableCell data-t="n" data-b-a-s="thin" data-a-h="center" />
                            <TableCell data-b-a-s="thin" data-a-h="center" />
                            <TableCell data-b-a-s="thin" data-a-h="center" />
                            <TableCell data-b-a-s="thin" data-a-h="center">
                                {Math.round(
                                    type === 0 ? annuity.totalAmount : differentiated.totalAmount,
                                ).toLocaleString(lang === 'kz' ? 'ru' : lang)}
                            </TableCell>
                        </TableRow>
                        {prepareItems().map((row, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    '& td': {
                                        border: '1px solid rgba(224, 224, 224, 1)',
                                        py: 1,
                                    },
                                }}
                            >
                                <TableCell data-t="n" data-b-a-s="thin" data-a-h="center">
                                    {index + 1}
                                </TableCell>
                                <TableCell data-b-a-s="thin" data-a-h="center">
                                    {new Date(row.date).toLocaleDateString(lang === 'kz' ? 'ru' : lang)}
                                </TableCell>
                                <TableCell data-b-a-s="thin" data-a-h="center">
                                    {Math.round(row.payment).toLocaleString(lang === 'kz' ? 'ru' : lang)}
                                </TableCell>
                                <TableCell data-b-a-s="thin" data-a-h="center">
                                    {Math.round(row.balance).toLocaleString(lang === 'kz' ? 'ru' : lang)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    ) : null;
};

export default PaymentSchedule;
